import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  DestroyRef,
  inject,
} from '@angular/core';
import { IBreadcrumb, ILink } from '../breadcrumbs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ILockValues, IProgressResponseItem, ITabs, ItemProgressKey } from '@shared/model/project';
import { IProjectConfig } from '@shared/model/projects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

enum NestedTabs {
  credits = 'loan-modeling',
  leasings = 'leasing-modeling',
  opexs = 'sales-and-revenue-calculation',
  products = 'cost-calculation',
  own_funds = 'modeling-of-equity-contributions',
  discount_rates = 'macroparameter-settings',
  taxs = 'setting-up-tax-calculations',
  working_capitals = 'modeling-working-capital',
  venture_funds = 'modeling-venture-funds',
}

enum NestedRoutTabs {
  'loan-modeling' = 'credits',
  'leasing-modeling' = 'leasings',
  'sales-and-revenue-calculation' = 'opexs',
  'cost-calculation' = 'products',
  'modeling-of-equity-contributions' = 'own_funds',
  'macroparameter-settings' = 'discount_rates',
  'setting-up-tax-calculations' = 'taxs',
  'modeling-working-capital' = 'working_capitals',
}
export const NestedRoutTabsObj = {
  'loan-modeling': 'credits',
  'leasing-modeling': 'leasings',
  'sales-and-revenue-calculation': 'opexs',
  'cost-calculation': 'products',
  'modeling-of-equity-contributions': 'own_funds',
  'macroparameter-settings': 'discount_rates',
  'setting-up-tax-calculations': 'taxs',
  'modeling-working-capital': 'working_capitals',
};

@Component({
  selector: 'app-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressBarComponent implements OnInit {
  protected destroyRef = inject(DestroyRef);
  private _id: number = this._route.snapshot.params['id'];
  private _tabs!: ITabs;

  get tabs() {
    return this._tabs;
  }

  @Input() set tabs(tabs: ITabs | null) {
    if (tabs) {
      this._tabs = tabs;
      this.isChildrenRouts(this.path);
    }
  }
  @Input() isLockValues!: ILockValues | null;
  @Input() progressData!: any;
  @Input() projectConfig!: IProjectConfig | null;

  path!: string;
  nestedBars: ILink[] = [];
  nestedValidBars: { [key: string]: boolean } = {};
  breadcrumbs: IBreadcrumb[] = [
    {
      link: 'basic-data',
      text: 'PROJECT_COMMON_MAIN_BAR',
      progressName: 'general_data',
    },
    {
      link: 'project-parameters',
      text: 'PROJECT_COMMON_PARAMS_BAR',
      progressName: 'taxes',
      children: [
        {
          link: 'setting-up-tax-calculations',
          text: 'PROJECT_COMMON_TAXES_BAR',
        },
        {
          link: 'macroparameter-settings',
          text: 'PROJECT_COMMON_DISCOUNT_RATE_BAR',
        },
      ],
    },
    {
      link: 'sales-and-costs',
      text: 'PROJECT_COMMON_SALES_COSTS_BAR',
      progressName: 'sales_and_opexs',
      children: [
        {
          link: 'sales-and-revenue-calculation',
          text: 'PROJECT_COMMON_SALES_REVENUE_BAR',
        },
        {
          link: 'cost-calculation',
          text: 'PROJECT_COMMON_COSTS_BAR',
        },
      ],
    },
    {
      link: 'capital-investments',
      text: 'PROJECT_COMMON_CAPEX_BAR',
      progressName: 'capexs',
    },
    {
      link: 'sources-of-funding',
      text: 'PROJECT_COMMON_FINANCE_BAR',
      progressName: 'fundings',
      children: [
        {
          link: 'modeling-of-equity-contributions',
          text: 'PROJECT_COMMON_EQUITY_BAR',
        },
        {
          link: 'loan-modeling',
          text: 'PROJECT_COMMON_LOAN_BAR',
        },
        {
          link: 'leasing-modeling',
          text: 'PROJECT_COMMON_LEASING_BAR',
        },
        {
          link: 'modeling-working-capital',
          text: 'PROJECT_COMMON_WORKING_CAPITAL_BAR',
        },
        {
          link: 'modeling-venture-funds',
          text: 'PROJECT_COMMON_VENTURE_BAR',
        },
      ],
    },
    {
      link: 'project-calculation',
      text: 'PROJECT_COMMON_CALCULATE_BAR',
      progressName: 'calculate',
      children: [
        {
          link: 'choice-of-calculation-options',
          text: 'PROJECT_COMMON_CALCULATION_OPTIONS_BAR',
        },
        {
          link: 'project-calculation',
          text: 'PROJECT_COMMON_PROJECT_CALCULATION_BAR',
        },
      ],
    },
  ];

  constructor(
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    let path = this._route?.snapshot?.firstChild?.routeConfig?.path;
    if (path) {
      this.path = path;
    }
    this._router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        let path = this._route.children[0].routeConfig?.path;
        this.nestedBars = [];
        if (path) {
          this.path = path;
          this.isChildrenRouts(path);
          let segments = this._router.url.split('/');
          let lastSegment = segments[segments.length - 1];
          if (lastSegment === 'sources-of-funding') {
            if (this.redirectTo() !== 'locked') {
              this._router.navigateByUrl(`project/${this._id}/sources-of-funding/${this.redirectTo()}`);
            }
          }
        }
      }
    });
  }

  isRender(name: string): boolean {
    if (this._tabs) {
      switch (name) {
        case 'modeling-working-capital':
          return this._tabs?.working_capital_tab;
        case 'modeling-of-equity-contributions':
          return this._tabs?.own_capitals_tab;
        case 'loan-modeling':
          return this._tabs?.credits_tab;
        case 'leasing-modeling':
          return this._tabs?.leasings_tab;
        case 'modeling-venture-funds':
          return this._tabs?.venture_tab;
        case 'capital-investments':
          return this._tabs?.capexs_tab;
        case 'sources-of-funding':
          return (
            this._tabs?.working_capital_tab ||
            this._tabs?.own_capitals_tab ||
            this._tabs?.credits_tab ||
            this._tabs?.leasings_tab ||
            this._tabs?.venture_tab
          );
        case 'macroparameter-settings':
          // NOTE: "Ставка дисконтирования" только для Професcионального режима
          return this.projectConfig?.mode === 'pro';
        default:
          return true;
      }
    } else {
      return true;
    }
  }

  checkProgressValue(progressName: ItemProgressKey | undefined) {
    if (progressName && this.progressData[progressName]) {
      return this.progressData[progressName][progressName];
    } else {
      return 0;
    }
  }

  checkValid(progressName: ItemProgressKey | undefined): boolean {
    if (progressName && this.progressData[progressName]) {
      this.nestedValidBars = { ...this.nestedValidBars, ...this.progressData[progressName].validated_tabs };
      return this.progressData[progressName].is_valid;
    } else {
      return false;
    }
  }

  checkNestedItemValid(path: keyof typeof NestedRoutTabsObj | string): boolean {
    if (path && path in NestedRoutTabsObj) {
      return this.nestedValidBars[NestedRoutTabsObj[path as keyof typeof NestedRoutTabsObj]];
    } else {
      return false;
    }
  }

  getItemProgress(progressName: ItemProgressKey) {
    return this.progressData?.filter((item: IProgressResponseItem) => item.hasOwnProperty(progressName));
  }

  isChildrenRouts(path: string): boolean {
    let current = this.breadcrumbs.find((item) => item.link === path);
    if (current?.children?.length) {
      if (path === 'project-calculation') {
        this.nestedBars = [];
      } else if (path === 'sources-of-funding') {
        this.nestedBars = current.children.filter((item) => {
          switch (item.link) {
            case 'leasing-modeling':
              return !!this.tabs?.leasings_tab;
            case 'modeling-working-capital':
              return !!this.tabs?.working_capital_tab;
            case 'modeling-of-equity-contributions':
              return !!this.tabs?.own_capitals_tab;
            case 'loan-modeling':
              return !!this.tabs?.credits_tab;
            case 'modeling-venture-funds':
              return !!this.tabs?.venture_tab;
            default:
              return false;
          }
        });
      } else {
        this.nestedBars = current.children;
      }
    }

    return !!this.nestedBars.length || !!current?.children?.length;
  }

  redirectTo(): string {
    let redirectedRoute = '';
    if (this.tabs)
      Object.entries(this.tabs).forEach((element) => {
        if (element[0] === 'capexs_tab' || element[0] === 'name') return;
        if (element[1]) {
          switch (element[0]) {
            case 'own_capitals_tab':
              redirectedRoute = 'modeling-of-equity-contributions';
              break;
            case 'credits_tab':
              redirectedRoute = 'loan-modeling';
              break;
            case 'leasings_tab':
              redirectedRoute = 'leasing-modeling';
              break;
            case 'working_capital_tab':
              redirectedRoute = 'modeling-working-capital';
              break;
            case 'venture_tab':
              redirectedRoute = 'modeling-venture-funds';
              break;
            case 'working_capital_tab':
              redirectedRoute = 'modeling-venture-funds';
              break;
          }
        }
      });
    return redirectedRoute;
  }

  disableLink(event: Event) {
    if ((event.target as HTMLElement).classList.contains('disabled')) {
      event.preventDefault();
    }
  }
}
